<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
    overlay-opacity="0.85"
    content-class="modern-response-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title class="editor-header d-flex justify-space-between align-center px-6 py-4">
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-message-reply-text</v-icon>
          Opportunity Response
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <!-- Main content -->
      <v-card-text class="px-6 py-4">
        <v-row>
          <!-- Need Information -->
          <v-col cols="12" class="py-2">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-2">
              Opportunity Details
            </div>
            <v-card outlined class="rounded-lg pa-4">
              <div v-if="need" class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-format-title</v-icon>
                <span class="text-body-1">{{ need.title }}</span>
              </div>
              <div v-if="agency" class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-office-building</v-icon>
                <span class="text-body-1">{{ agency.name }}</span>
              </div>
            </v-card>
          </v-col>
          
          <!-- Responder Information -->
          <v-col cols="12" class="py-2">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-2">
              Your Information
            </div>
            <v-card outlined class="rounded-lg pa-4">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                <span class="text-body-1">
                  {{ profile.type === 'Volunteer' ? 
                    `${profile.first_name} ${profile.last_name}` : 
                    profile.name }}
                </span>
              </div>
            </v-card>
          </v-col>
          
          <!-- Response Notes -->
            <v-col cols="12" class="py-2">
            <div class="text-subtitle-1 font-weight-medium primary--text mb-2 d-flex align-center">
              <span>Your Notes</span>
              <tip-icon class="ml-2">
              Type any additional notes that you feel would be of use to the agency manager.
              (Examples include preferences, restrictions, and further availability details.)
              </tip-icon>
            </div>
            <v-textarea
              v-model="note"
              label="Notes"
              outlined
              dense
              rows="3"
              counter="500"
              maxlength="500"
              prepend-inner-icon="mdi-note-text"
              :error-messages="noteMessage"
              :disabled="status === 'Rejected'"
              @input="onChange"
              class="modern-input"
              :rules="[rules.maxLength]"
            />
            </v-col>
          
          <!-- Status Information -->
          <template v-if="already">
            <v-col cols="12" class="py-2" v-if="reply">
              <div class="text-subtitle-1 font-weight-medium primary--text mb-2">
                Agency Response
              </div>
              <v-card outlined class="rounded-lg pa-4">
                <div class="text-body-1">{{ reply }}</div>
              </v-card>
            </v-col>
            
            <v-col cols="12" class="py-2" v-if="status">
              <div class="text-subtitle-1 font-weight-medium primary--text mb-2">
                Current Status
              </div>
              <v-chip
                :color="statusColor"
                dark
                large
              >
                {{ status }}
              </v-chip>
            </v-col>
          </template>
        </v-row>
        
        <v-expand-transition>
          <v-alert
            v-if="error"
            dense
            type="error"
            class="mt-4"
            transition="scale-transition"
          >
            {{ error }}
          </v-alert>
        </v-expand-transition>
      </v-card-text>
      
      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 d-flex justify-space-between">
        <v-btn
          text
          @click="onClose"
          color="grey darken-1"
          class="px-6"
          large
        >
          <v-icon left>mdi-close</v-icon>
          Close
        </v-btn>
        
        <div>
          <v-btn
         v-if="already && status !== 'Rejected' && !isTimerRunningForThisShift"
            color="error"
            text
            :loading="cancelling"
            @click="onWithdrawResponse"
            class="px-6 mr-3"
            large
          >
            <v-icon left>mdi-cancel</v-icon>
            Withdraw
          </v-btn>
          
          <v-btn
            v-if="status !== 'Rejected'"
            color="primary"
            :loading="loading"
            @click="onSubmitNeedResponse"
            class="px-6 save-btn"
            large
            dark
          >
            <template v-slot:loader>
              <span class="custom-loader">
                <v-icon light>mdi-autorenew</v-icon>
              </span>
            </template>
            <v-icon left>{{ already ? 'mdi-pencil' : 'mdi-send' }}</v-icon>
            {{ already ? 'Update' : 'Submit' }}
          </v-btn>
        </div>
      </v-card-actions>
      
      <!-- Snackbar for notifications -->
      <v-snackbar
        v-model="snackbar"
        :timeout="3000"
        color="primary"
        top
      >
        {{ snackMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn
            text
            v-bind="attrs"
            @click="snackbar = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import DOMPurify from "dompurify";
import TipIcon from "@/components/common/TipIcon.vue";
import { mapActions, mapState } from "vuex";

export default {
  components: {
    TipIcon
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    need: Object,
    shift: Object,
    onCloseDialog: {
      type: Function
    },
    onResponse: {
      type: Function
    },
    onCancelled: {
      type: Function
    }
  },
  data() {
    return {
      loading: false,
      snackbar: false,
      snackMessage: null,
      error: null,
      note: null,
      agency: null,
      noteMessage: null,
      already: false,
      cancelling: false,
      status: null,
      reply: null,
      rules: {
        // Notes is not mandatory, so we don't need required rule
        maxLength: value => !value || value.length <= 500 || 'Maximum 500 characters allowed'
      }
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    ...mapState("need", {
  timerInfo: "timerInfo"
}),
    statusColor() {
      switch(this.status) {
        case 'Approved': return 'success';
        case 'Pending': return 'warning';
        case 'Rejected': return 'error';
        default: return 'grey';
      }
    },
    isTimerRunningForThisShift() {
      return this.timerInfo?.res && 
             this.timerInfo.res.status === 'Tracking' && 
             this.timerInfo.res.shift_id === this.shift?._id;
    }
  },
  methods: {
    ...mapActions({
      submitResponse: "need/respondShift",
      cancelResponse: "need/cancelResponse",
      onUpdateResponseNote: "need/onUpdateResponseNote",
      getAgency: "agency/getAgency",
      findTimer: "timeslot/findTimer"
    }),
    onClose() {
      this.note = null;
      this.error = null;
      this.onCloseDialog();
    },
    onChange() {
      this.error = null;
    },
        // Utility sanitizer
    sanitizeInput(value) {
      if (!value) return value;

      // Step 1: Sanitize unsafe HTML but allow text inside <script> to survive
      let clean = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });

      // Step 2: If DOMPurify returns empty string, fallback to extracting inner text
      if (!clean.trim()) {
        // Create a temporary div to extract text content
        const temp = document.createElement("div");
        temp.innerHTML = value;
        clean = temp.textContent || temp.innerText || "";
      }

      return clean.trim();
    },
    async onSubmitNeedResponse() {
      // Notes is not mandatory, pass empty string if not entered
      const noteToSend = this.note || '';
      try {
        this.loading = true;
        if (this.already) {
          const res = await this.onUpdateResponseNote({
            need_id: this.need._id,
            shift_id: this.shift._id,
            note: this.sanitizeInput(noteToSend)
          });
          this.onResponse(res);
        } else {
          const res = await this.submitResponse({
            need_id: this.need._id,
            shift_id: this.shift._id,
            note: this.sanitizeInput(noteToSend)
          });
          this.onResponse(res);
        }
      } catch (error) {
        this.error = error.response?.data?.message || "An error occurred";
      } finally {
        this.loading = false;
      }
    },
        async findTimerInfo() {
      try {
        const timerInfo2 = await this.findTimer();
        if (timerInfo2) {
          this.$store.commit("need/setTimerInfo", {
            ...timerInfo2,
            shiftHours: this.shift?.hours
          });
          return timerInfo2;
        }
      } catch (e) {
        // handle error if needed
        console.log(e, "Error finding timer info");
      }
    },
    async onWithdrawResponse() {
      try {
        this.cancelling = true;
        const res = await this.cancelResponse({ _id: this.shift._id });
        this.onCancelled(res);
      } catch (error) {
        this.snackbar = true;
        this.snackMessage = error.response?.data?.message || "Failed to withdraw";
      } finally {
        this.cancelling = false;
      }
    },
    async loadAgency() {
      if (this.need?.agency) {
        try {
          this.agency = await this.getAgency({ _id: this.need.agency });
        } catch (error) {
          console.error("Failed to load agency", error);
        }
      }
    },
    checkExistingResponse() {
      this.already = false;
      if (this.shift?.responses?.length) {
        const userResponse = this.shift.responses.find(
          response => response.user_id === this.profile._id
        );
        if (userResponse) {
          this.already = true;
          this.note = userResponse.note;
          this.reply = userResponse.reply;
          this.status = userResponse.status;
        }
      }
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.loadAgency();
        this.checkExistingResponse();
         this.findTimerInfo();
      }
    },
    //     timerInfo: {
    //   handler(newVal, oldVal) {
    //     console.log('TimerInfo changed:', {
    //       old: oldVal,
    //       new: newVal,
    //       shiftId: this.shift?._id
    //     });
    //   },
    //   deep: true
    // },
    need: {
      handler() {
        this.loadAgency();
      },
      immediate: true
    }
  },
  mounted() {
    this.findTimerInfo();
  },
};
</script>

<style scoped>
.modern-response-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255,255,255,0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 2px solid #e0e0e0; */
  background-color: white !important;
}

/* .modern-input::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
} */

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-response-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-response-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-response-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-response-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}

@media (max-width: 600px) {
  .v-card__actions {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}
</style>